import { buildCategoryColorMap } from "./colors";
import { collectDimensionKeys, sumValues } from "./aggregate";
import { toEnergyAnalysisLabel } from "./labels";

import type {
  EnergyAnalysisCategoryTotal,
  EnergyAnalysisMixCell,
  EnergyAnalysisRow,
  EnergyAnalysisYearlyTableRow,
} from "../model/types";

export const collectMixAxes = (rows: EnergyAnalysisRow[]) => ({
  useTypes: collectDimensionKeys(rows, "useType"),
  utilities: collectDimensionKeys(rows, "utility"),
});

export const buildMixCells = (rows: EnergyAnalysisRow[]): EnergyAnalysisMixCell[] => {
  const { useTypes, utilities } = collectMixAxes(rows);
  const utilityColors = buildCategoryColorMap(utilities);
  const cells: EnergyAnalysisMixCell[] = [];
  useTypes.forEach((useType) => {
    utilities.forEach((utility) => {
      const value = sumValues(rows.filter((row) => row.useType === useType && row.utility === utility));
      if (value <= 0) return;
      const color = utilityColors[utility];
      cells.push({
        color: color ?? "var(--color-foreground-primary)",
        useType,
        useTypeLabel: toEnergyAnalysisLabel(useType),
        utility,
        utilityLabel: toEnergyAnalysisLabel(utility),
        value,
      });
    });
  });
  return cells.sort((left, right) => right.value - left.value);
};

export const buildMixCategoryTotals = (
  cells: EnergyAnalysisMixCell[],
  totalValue: number
): EnergyAnalysisCategoryTotal[] =>
  cells.map((cell) => ({
    color: cell.color,
    key: `${cell.useType}-${cell.utility}`,
    label: `${cell.useTypeLabel} · ${cell.utilityLabel}`,
    share: totalValue === 0 ? 0 : cell.value / totalValue,
    value: cell.value,
  }));

export const buildMixTableRows = (
  cells: EnergyAnalysisMixCell[],
  useTypes: string[],
  utilities: string[]
): EnergyAnalysisYearlyTableRow[] =>
  useTypes.map((useType) => {
    const useTypeCells = cells.filter((cell) => cell.useType === useType);
    const yearlyRow: EnergyAnalysisYearlyTableRow = {
      id: useType,
      rowLabel: toEnergyAnalysisLabel(useType),
      value: useTypeCells.reduce((total, cell) => total + cell.value, 0),
    };
    utilities.forEach((utility) => {
      const match = useTypeCells.find((cell) => cell.utility === utility);
      yearlyRow[utility] = match?.value ?? 0;
    });
    return yearlyRow;
  });
